import { useEffect, useMemo, useState } from 'react';
import { Check, ChevronsUpDown, Loader2, X } from 'lucide-react';
import { useList } from '@refinedev/core';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { User } from '@/resources/users/types';

interface UserSearchComboboxProps {
  /** Текущий выбранный пользователь (контролируемый компонент). */
  value: User | null;
  onChange: (user: User | null) => void;
  /** Текст-подсказка при пустом значении. */
  placeholder?: string;
  /** Отключить компонент (например, во время сабмита). */
  disabled?: boolean;
  /** Отфильтровать пользователей из результатов поиска (например, исключить самого себя). */
  filter?: (user: User) => boolean;
  /** Показать только юзеров с telegramId (для привязки EMAIL-инвайта к Telegram-аккаунту). */
  requireTelegramId?: boolean;
  /** Дополнительный CSS-класс для триггера. */
  className?: string;
}

/**
 * Combobox с поиском пользователей через GET /api/users?search=…
 * Минимум 2 символа, debounce 300ms, до 20 кандидатов на запрос.
 */
export function UserSearchCombobox({
  value,
  onChange,
  placeholder = 'Поиск по имени, email, телеграму…',
  disabled = false,
  filter,
  requireTelegramId = false,
  className,
}: UserSearchComboboxProps): React.ReactElement {
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  const listResult = useList<User>({
    resource: 'users',
    pagination: { currentPage: 1, pageSize: 20, mode: 'server' },
    filters: useMemo(
      () =>
        debouncedSearch.length > 0
          ? [{ field: 'search', operator: 'contains' as const, value: debouncedSearch }]
          : [],
      [debouncedSearch],
    ),
    queryOptions: {
      enabled: popoverOpen && debouncedSearch.length >= 2,
      retry: false,
    },
  });

  const candidates: User[] = useMemo(() => {
    const data = listResult.result?.data ?? [];
    let result = data;
    if (filter) {
      result = result.filter(filter);
    }
    if (requireTelegramId) {
      result = result.filter((u) => Boolean(u.telegramId));
    }
    return result;
  }, [listResult.result?.data, filter, requireTelegramId]);

  return (
    <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={popoverOpen}
          className={cn('w-full justify-between', className)}
          disabled={disabled}
        >
          {value ? (
            <span className="flex items-center gap-2 truncate">
              <span className="truncate">{formatUserLine(value)}</span>
            </span>
          ) : (
            <span className="text-muted-foreground">{placeholder}</span>
          )}
          {value ? (
            <X
              className="ml-2 h-4 w-4 shrink-0 opacity-50 hover:opacity-100"
              onClick={(e) => {
                e.stopPropagation();
                onChange(null);
              }}
            />
          ) : (
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[var(--radix-popover-trigger-width)] p-0"
        align="start"
      >
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Введите минимум 2 символа…"
            value={search}
            onValueChange={setSearch}
          />
          <CommandList>
            {debouncedSearch.length < 2 && (
              <CommandEmpty>Введите минимум 2 символа для поиска.</CommandEmpty>
            )}
            {debouncedSearch.length >= 2 && listResult.query.isLoading && (
              <div className="flex items-center justify-center gap-2 py-4 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                Поиск…
              </div>
            )}
            {debouncedSearch.length >= 2 &&
              !listResult.query.isLoading &&
              candidates.length === 0 && (
                <CommandEmpty>Ничего не найдено.</CommandEmpty>
              )}
            {candidates.length > 0 && (
              <CommandGroup>
                {candidates.map((u) => (
                  <CommandItem
                    key={u.id}
                    value={String(u.id)}
                    onSelect={() => {
                      onChange(u);
                      setPopoverOpen(false);
                    }}
                    className="flex flex-col items-start gap-0.5"
                  >
                    <div className="flex w-full items-center gap-2">
                      <Check
                        className={cn(
                          'h-4 w-4 shrink-0',
                          value?.id === u.id ? 'opacity-100' : 'opacity-0',
                        )}
                      />
                      <span className="font-medium">{formatUserLine(u)}</span>
                    </div>
                    <div className="ml-6 text-xs text-muted-foreground">
                      #{u.id} · {formatUserSecondary(u)}
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function formatUserLine(u: User): string {
  const name = [u.firstName, u.lastName].filter(Boolean).join(' ').trim();
  if (name) {
    return name;
  }
  if (u.username) {
    return `@${u.username}`;
  }
  if (u.email) {
    return u.email;
  }
  if (u.telegramId) {
    return `tg:${u.telegramId}`;
  }
  return `#${u.id}`;
}

function formatUserSecondary(u: User): string {
  const parts: string[] = [];
  if (u.username) {
    parts.push(`@${u.username}`);
  }
  if (u.email) {
    parts.push(u.email);
  }
  if (u.telegramId) {
    parts.push(`tg:${u.telegramId}`);
  }
  if (u.phone) {
    parts.push(u.phone);
  }
  return parts.length > 0 ? parts.join(' · ') : 'нет контактов';
}
